import { Box, Button, HStack, Text } from "@chakra-ui/react";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";

import { PlusIcon } from "@nens/ranalib";

import DurationInput from "../components/DurationInput";
import NumberInput from "../components/NumberInput";
import SelectCustomSimple from "../components/SelectCustomSimple";
import PumpIcon from "../icons/pump_icon.svg?react";
import { checkPermission } from "../state/appState";
import { Tool, setActiveTool } from "../state/mainGui";
import {
  getAmountOfWater,
  getAmountUnit,
  getDuration,
  getPlaceOnMapMode,
  setAmountOfWater,
  setAmountUnit,
  setDuration,
  setPlaceOnMapMode,
} from "../state/pumpMenu";
import ErrorMessageStyles from "../styles/ErrorMessage.module.css";
import FormStyles from "../styles/Forms.module.css";
import { buttonRibbonStyle } from "../styles/styleComposition";
import { formattedMessageForErrorCode } from "../utils/authorizationUtils";
import { AmountUnit, allAmountUnits } from "../utils/conversions";

import CollapsableSidebarStyles from "./CollapsableSidebar.module.css";
import ExistingDischarges from "./existing/ExistingDischarges";
import { FormLabel } from "./FormLabel";
import LeftMenuHeader from "./LeftMenuHeader";
import menuStyles from "./Menu.module.css";

interface Props {
  setAmountOfWater: (amountOfWaterPerSecond: string) => void;
  setAmountUnit: (amountUnit: AmountUnit) => void;
  setDuration: (duration: string) => void;
  setActiveTool: (tool: Tool) => void;
  setPlaceOnMapMode: (bool: boolean) => void;
}

const PumpMenu: React.FC<Props> = (props) => {
  const intl = useIntl();
  const amountOfWaterPerSecond = useSelector(getAmountOfWater);
  const amountUnit = useSelector(getAmountUnit);
  const duration = useSelector(getDuration);

  const placeOnMapMode = useSelector(getPlaceOnMapMode);

  const permission = useSelector(checkPermission)("editDischarge");

  const disableWholeForm = permission.allowed === false;
  const disablesWholeFormErrorMessage =
    permission.allowed === false && formattedMessageForErrorCode(permission.errorCode, intl);

  if (
    permission.allowed === false &&
    permission.errorCode === "simulationRunning" &&
    placeOnMapMode === true
  ) {
    props.setPlaceOnMapMode(false);
  }

  return (
    <div className={`${CollapsableSidebarStyles.LeftMenu}`}>
      <LeftMenuHeader Icon={PumpIcon}>
        <FormattedMessage id="pumpMenu.header" defaultMessage="Pump" />
      </LeftMenuHeader>
      <div
        className={`${menuStyles.SubMenu} ${menuStyles.SubMenuStaticContent} ${menuStyles.List}`}
        style={{ padding: "0 8px 0 8px" }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={FormStyles.NormalForm}
        >
          <fieldset disabled={disableWholeForm}>
            <FormLabel>
              <FormattedMessage
                id="newPumpMenu.amount_water_label"
                defaultMessage="Amount of water"
              />
            </FormLabel>
            <HStack>
              <Box w="45%">
                <NumberInput
                  value={amountOfWaterPerSecond + ""}
                  unit={""}
                  setValue={(value: string) => {
                    props.setAmountOfWater(value);
                  }}
                  zeroIsInvalid
                  allowDecimals
                  required
                />
              </Box>
              <Box>
                <SelectCustomSimple
                  value={amountUnit}
                  onChange={(e) => {
                    const value = e.target.value;
                    // how should I do this 'value as AmountUnit' he right way?
                    props.setAmountUnit(value as AmountUnit);
                  }}
                >
                  {allAmountUnits.map((unit) => {
                    return (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    );
                  })}
                </SelectCustomSimple>
              </Box>
            </HStack>
            <FormLabel>
              <FormattedMessage id="pumpMenu.duration_label" defaultMessage="Duration" />
            </FormLabel>
            <DurationInput value={duration} setValue={props.setDuration} />
            {!placeOnMapMode ? (
              <div className={buttonRibbonStyle}>
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    const target = e.target as HTMLButtonElement;
                    if (target.form && target.form.checkValidity && target.form.checkValidity()) {
                      props.setPlaceOnMapMode(true);
                    }
                  }}
                  leftIcon={<PlusIcon />}
                >
                  <FormattedMessage
                    id="newPumpMenu.place_on_map_button"
                    defaultMessage="Place on map"
                  />
                </Button>
              </div>
            ) : (
              <div className={buttonRibbonStyle}>
                <Button
                  variant="secondary"
                  onClick={() => props.setPlaceOnMapMode(false)}
                  margin="8px 0 8px 0"
                >
                  <FormattedMessage
                    id="newPumpMenu.place_on_map_cancel_button"
                    defaultMessage="Cancel"
                  />
                </Button>
              </div>
            )}
            <Text
              color="brand.analytic"
              fontSize="16px"
              visibility={placeOnMapMode ? "visible" : "hidden"}
            >
              <FormattedMessage
                id="dischargeMenu.pump_drawing_mode_place_cursor_on_map_helptext"
                defaultMessage="Click on the map to place the pump."
              />
            </Text>

            <div className={`${menuStyles.ListItem} `}>
              {disablesWholeFormErrorMessage ? (
                <span className={ErrorMessageStyles.ErrorMessageStyle}>
                  {disablesWholeFormErrorMessage}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
      <ExistingDischarges isPump />
    </div>
  );
};

export default connect(null, {
  setActiveTool,
  setAmountOfWater,
  setAmountUnit,
  setDuration,
  setPlaceOnMapMode,
})(PumpMenu);
